import React, { useState } from "react";
// import { Link } from "react-router-dom";
import { useRegisterStudent } from "../../../../hooks/api/Students/Auth/useRegisterStudent";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import Button from "../../../../components/Button";
import { Eye, EyeOff } from "lucide-react";
import { Checkbox, Modal, message } from "antd";
import { studentAgreement } from "../../../../agreements";

const StudentSignUpForm = () => {
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [payload, setPayload] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    password: "",
    isParent: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPayload((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const { mutateAsync, isLoading } = useRegisterStudent(payload);

  const handleSubmit = () => {
    if (!acceptedTerms) {
      message.error(
        "Please accept the Student terms and conditions to proceed"
      );
      return;
    }
    mutateAsync();
  };

  const isFormComplete = (formData: Record<string, any>): boolean => {
    return Object.entries(formData)
      .filter(([key]) => key !== "isParent") // Exclude 'isParent' from validation
      .every(([, value]) => typeof value === "string" && value.trim() !== "");
  };

  return (
    <div className="mx-auto overflow-y-scroll">
      <h3 className="text-gray900 text-txs W2STMedium mb-3">
        Create a student account
      </h3>

      <div className="mx-auto">
        <div className="flex gap-3 mb-5">
          <span className="w-full">
            <label htmlFor="firstname" className="text-gray700">
              First Name
            </label>
            <input
              name="firstName"
              type="text"
              value={payload.firstName}
              placeholder="Enter first name"
              className="border-[1px] rounded-md border-gray300 p-4 block mt-3 outline-none w-full"
              onChange={handleChange}
              disabled={isLoading}
            />
          </span>
          <span className="w-full">
            <label htmlFor="lastname" className="text-gray700">
              Last Name
            </label>
            <input
              name="lastName"
              type="text"
              value={payload.lastName}
              placeholder="Enter last name"
              className="border-[1px] rounded-md border-gray300 p-4 block mt-3 outline-none w-full"
              onChange={handleChange}
              disabled={isLoading}
            />
          </span>
        </div>
        <div className="w-full mb-5">
          <label htmlFor="email" className="text-gray700">
            Email
          </label>
          <input
            name="email"
            type="email"
            value={payload.email}
            placeholder="Enter your email"
            className="border-[1px] rounded-md border-gray300 p-4 block mt-3 outline-none w-full"
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>
        <div className="w-full mb-5">
          <label htmlFor="phone" className="text-gray700">
            Phone
          </label>
          {/* <input
              name="phoneNumber"
              type="number"
              value={payload.phoneNumber}
              placeholder="Enter your phone number"
              className="border-[1px] rounded-md border-gray300 p-4 block mt-3 outline-none w-full"
              onChange={handleChange}
            /> */}
          <PhoneInput
            defaultCountry="us"
            className="mt-[13px]"
            name="phoneNumber"
            value={payload?.phoneNumber}
            onChange={(phone) => {
              setPayload({
                ...payload,
                phoneNumber: phone,
              });
            }}
            disabled={isLoading}
          />
        </div>
        <div className="w-full">
          <label htmlFor="password" className="text-gray700">
            Create password
          </label>
          <div className="relative">
            <input
              name="password"
              value={payload.password}
              type={passwordVisibility ? "text" : "password"}
              placeholder="Enter your password"
              className="border-[1px] rounded-md border-gray300 p-4 block mt-3 outline-none w-full"
              onChange={handleChange}
              disabled={isLoading}
            />

            <span
              className="absolute right-6 top-5 flex text-gray400 items-center text-sm cursor-pointer"
              onClick={() => setPasswordVisibility(!passwordVisibility)}
            >
              {passwordVisibility ? <EyeOff size={20} /> : <Eye size={20} />}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 my-4">
          <input
            type="checkbox"
            checked={payload?.isParent}
            className="accent-[#A54CE8] h-4 w-4 cursor-pointer"
            onChange={(e) => {
              setPayload({
                ...payload,
                isParent: e.target.checked,
              });
            }}
          />
          <span className="text-sm">
            I am a parent registering on behalf of my child
          </span>
        </div>
        <Modal
          centered
          width={700}
          footer={null}
          open={showTermsModal}
          onCancel={() => setShowTermsModal(false)}
        >
          <div className="space-y-4">
            <h3 className="text-[20px] W2STMedium">
              Student Terms & Responsibilities
            </h3>
            <div className="h-[300px] overflow-y-auto whitespace-pre-wrap text-sm text-gray-700 border rounded-md p-4 bg-gray-50">
              {studentAgreement}
            </div>
            <div className="flex items-center justify-between">
              {/* <span className="text-sm text-gray-600">
                View general terms at{" "}
                <Link to="/terms" className="text-primary600 underline">
                  Terms & Conditions
                </Link>
              </span> */}
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => {
                  setAcceptedTerms(e.target.checked);
                  setTimeout(() => {
                    setShowTermsModal(false);
                  }, 300);
                }}
              >
                I have read and agree
              </Checkbox>
            </div>
          </div>
        </Modal>
        <div
          className="flex items-center gap-2 my-4 cursor-pointer"
          onChange={(e) => {
            setShowTermsModal(true);
          }}
        >
          <input
            type="checkbox"
            checked={acceptedTerms}
            className="accent-[#A54CE8] h-4 w-4 cursor-pointer"
          />
          <span className="text-sm">
            Click to read the Student terms and conditions
          </span>
        </div>
        <div className="block">
          <Button
            label="Create Account"
            isFormValid={isFormComplete(payload) && acceptedTerms}
            isLoading={isLoading}
            handleSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default StudentSignUpForm;
